import { ConnectDropTarget, DropTargetMonitor, useDrop } from 'react-dnd';
import { useDispatch } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { CAROUSEL_ITEM_SLIDE_DRAG_TYPE } from 'components/CarouselItemSlide/useCarouselItemSlideDragSource';
import { dropLayoutFn, LayoutDragSourceTypes } from 'components/Section/dnd';
import { DragSourceType } from 'const';
import { changeCarouselSlidePosition } from 'containers/Layouts/actions';
import { CAROUSEL_ITEM_TYPES, CarouselItem, CarouselItemType, DROP_TARGET_LAYOUTS_CAROUSEL_ITEM_TYPES } from 'containers/Layouts/sagas/addCarousel';

type Props = {
  sectionId: string;
  type: CarouselItemType;
  carouselId: string;
  sectionDisplayName: string;
  slideId?: string;
  slideNumber?: number;
};

type DropFnProps = {
  slideId?: string;
  slideNumber?: number;
  sectionId: string;
  carouselId: string;
  monitor: DropTargetMonitor;
  dispatch: Dispatch<AnyAction>;
  dropTargetCarouselItemType: CarouselItemType;
};

const dropFunction = (props: DropFnProps): void => {
  const { carouselId, monitor, dispatch, dropTargetCarouselItemType, sectionId, slideId, slideNumber } = props;
  if (monitor.getItemType() === CAROUSEL_ITEM_TYPES.SLIDE && slideNumber) {
    const sourceSlide = monitor.getItem() as CAROUSEL_ITEM_SLIDE_DRAG_TYPE;
    dispatch(changeCarouselSlidePosition(sourceSlide, slideNumber));

    return;
  }
  const carouselItem: CarouselItem = {
    type: dropTargetCarouselItemType,
    carouselId,
    slideId,
    slideNumber,
  };

  dropLayoutFn(sectionId, monitor, dispatch, carouselItem);
};

type CarouselItemDropTargetHook = {
  canDrop: boolean;
  isOver: boolean;
  drop: ConnectDropTarget;
};

export const useCarouselItemDropTarget = (props: Props): CarouselItemDropTargetHook => {
  const dispatch = useDispatch();
  const { type, carouselId, sectionId, slideId, slideNumber } = props;

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [...LayoutDragSourceTypes, CAROUSEL_ITEM_TYPES.SLIDE],
    canDrop: (_, monitor: DropTargetMonitor) => {
      if (!monitor.isOver({ shallow: true })) {
        return false;
      }
      const draggingItemType = monitor.getItemType() as DragSourceType | CAROUSEL_ITEM_TYPES.SLIDE;

      if (draggingItemType === CAROUSEL_ITEM_TYPES.SLIDE) {
        const draggingSlide = monitor.getItem() as CAROUSEL_ITEM_SLIDE_DRAG_TYPE;
        const draggingSlideId = draggingSlide.slideId;
        if (carouselId !== draggingSlide.carouselId) {
          return false;
        }

        return Boolean(slideId && (slideId !== draggingSlideId));
      }
      if (!LayoutDragSourceTypes.includes(draggingItemType)) {
        return false;
      }
      if (!DROP_TARGET_LAYOUTS_CAROUSEL_ITEM_TYPES.includes(type)) {
        return false;
      }

      return draggingItemType !== DragSourceType.CAROUSEL;
    },
    drop: (_, monitor) => dropFunction({ sectionId, carouselId, monitor, dispatch, dropTargetCarouselItemType: type, slideId, slideNumber }),
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
      dropItem: monitor.getItem(),
    }),
  });

  return {
    canDrop,
    isOver,
    drop,
  };
};
